/**
 * "Since you last looked (Oct 5): ..." — what moved on an item between the
 * reader's previous open and this one (card 10a54f95).
 *
 * Jess opens the same item many times in a few days; the UX loop reopened
 * QT-10026 and had to read the whole brief and scroll twice to learn that
 * nothing had moved. The server keeps one row per open (signal_thread_views)
 * and answers the open just recorded with the one before it; this diffs what
 * the screen can see against that mark:
 *
 *  1. the item's timeline rows written after it — people's actions and the
 *     delivery lines in full, Merlin's housekeeping folded into one count,
 *     a colleague's reply to the hand-off email named as such;
 *  2. answers on its asks that arrived after it (an ask's responses carry
 *     when and from whom);
 *  3. the newest hand-off reply the item carries, when the timeline window
 *     does not already hold it.
 *
 * Read newest first the history window always holds the newest rows, so a
 * window cut short before the mark (every row in it is newer) says so:
 * older rows may be new too (`more`).
 *
 * Pure logic (repo convention: logic in .ts, components in .tsx).
 */
import type { DoorThreadEventRead, DoorThreadRead } from '../../services/qcpInvestigations';
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';
import type { TimelineWindow } from './deliveryView';
import { foldedLabel, isHousekeeping } from './timelineView';

export interface SinceItem {
  /** When it happened (ISO). */
  at: string;
  /** One short line: "Cliff Platt answered “Which lots?”". */
  text: string;
  kind: 'timeline' | 'housekeeping' | 'ask_answer' | 'route_reply';
}

export interface SinceLastLook {
  /** The previous open (ISO). */
  since: string;
  /** Newest first; empty means nothing moved. */
  items: SinceItem[];
  /** The history window stopped before reaching the mark: rows older than
   *  what is loaded may be new as well. */
  more: boolean;
}

const RANK: Record<SinceItem['kind'], number> = { ask_answer: 0, route_reply: 0, timeline: 1, housekeeping: 2 };

function ms(iso: string | null | undefined): number {
  const t = iso ? new Date(iso).getTime() : NaN;
  return Number.isNaN(t) ? NaN : t;
}

/** "Jess Finlaw: Sent to Laura instead" — the row's who, unless the summary
 *  already names them ("Merlin started tracking this thread"). */
function timelineLine(ev: DoorThreadEventRead): string {
  const who = (ev.actor_label ?? '').trim() || (ev.actor_kind === 'merlin' ? 'Merlin' : '');
  const summary = ev.summary.trim();
  if (!who || summary.toLocaleLowerCase().startsWith(who.toLocaleLowerCase())) return summary;
  return `${who}: ${summary}`;
}

/** The person an answer on this ask came from (respondedView's rule): the
 *  person it was asked of, or for a team / company ask whoever answered. */
function answeredBy(ask: DoorDataRequestRead, responder: string): string {
  // The daily sweep found the answer elsewhere (kanban 3eed0be5): nobody replied.
  if (responder.startsWith('merlin:')) return 'Merlin';
  return ask.audience === 'team' || ask.audience === 'company'
    ? responder || ask.target_name
    : ask.target_name;
}

export function sinceLastLook(
  lastSeen: string | null | undefined,
  window: TimelineWindow<DoorThreadEventRead> | null | undefined,
  asks: ReadonlyArray<DoorDataRequestRead>,
  thread?: Pick<DoorThreadRead, 'latest_route_reply'> | null,
): SinceLastLook | null {
  const mark = ms(lastSeen);
  if (Number.isNaN(mark) || !window) return null;

  const items: SinceItem[] = [];
  const newer = window.events.filter((e) => ms(e.created_at) > mark);
  const housekeeping = newer.filter(isHousekeeping);
  if (housekeeping.length > 0) {
    const latest = housekeeping.reduce((a, b) => (ms(b.created_at) > ms(a.created_at) ? b : a));
    items.push({ at: latest.created_at, text: foldedLabel(housekeeping), kind: 'housekeeping' });
  }
  let sawReply = false;
  for (const ev of newer) {
    if (isHousekeeping(ev)) continue;
    if (ev.event_type === 'route_reply_received') {
      sawReply = true;
      const who = ev.reply?.from_name?.trim() || 'They';
      items.push({ at: ev.created_at, text: `${who} replied to the hand-off email`, kind: 'route_reply' });
    } else {
      items.push({ at: ev.created_at, text: timelineLine(ev), kind: 'timeline' });
    }
  }
  // Older than the window and newer than the mark: the window ended before
  // reaching the mark, so rows past it may be new as well. Read oldest first
  // with more past it, the newest rows are missing and nothing can be said.
  const more =
    window.has_more &&
    (window.order === 'asc' || window.events.length === 0 || newer.length === window.events.length);

  const reply = thread?.latest_route_reply;
  if (!sawReply && reply && ms(reply.received_at) > mark) {
    items.push({
      at: reply.received_at as string,
      text: `${reply.from_name.trim() || 'They'} replied to the hand-off email`,
      kind: 'route_reply',
    });
  }

  for (const ask of asks) {
    const answers = ask.responses.filter((r) => ms(r.created_at) > mark);
    const latest = answers.reduce<{ at: string; who: string } | null>(
      (best, r) => (!best || ms(r.created_at) > ms(best.at) ? { at: r.created_at, who: r.responder_identifier } : best),
      null,
    );
    const fallback =
      !latest && ask.status === 'answered' && ms(ask.answered_at) > mark
        ? { at: ask.answered_at as string, who: '' }
        : null;
    const hit = latest ?? fallback;
    if (!hit) continue;
    items.push({
      at: hit.at,
      text: `${answeredBy(ask, hit.who)} answered “${ask.question_title}”`,
      kind: 'ask_answer',
    });
  }

  // What Jess waits on first: people's answers and replies, then what
  // people did on the item, then Merlin's housekeeping; newest first within.
  items.sort((a, b) => RANK[a.kind] - RANK[b.kind] || ms(b.at) - ms(a.at));
  return { since: lastSeen as string, items, more };
}

/** "today, 9:15 AM" / "yesterday" / "Oct 5" for the "(…)" after the label. */
export function lastLookLabel(since: string, now: Date = new Date()): string {
  const t = ms(since);
  if (Number.isNaN(t)) return '';
  const d = new Date(t);
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(d, now)) {
    return `today, ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(d, yesterday)) return 'yesterday';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  });
}

/** The tag on each line: what kind of thing moved. */
export function sinceKindLabel(kind: SinceItem['kind']): string {
  switch (kind) {
    case 'ask_answer':
      return 'Answer';
    case 'route_reply':
      return 'Reply';
    case 'housekeeping':
      return 'Merlin';
    default:
      return 'Update';
  }
}

/** "3 changes since you last looked" / "Nothing new since you last looked";
 *  "3+" when the loaded history ends before the mark. */
export function sinceHeadline(count: number, more = false): string {
  if (count === 0) return 'Nothing new since you last looked';
  return `${count}${more ? '+' : ''} ${count === 1 && !more ? 'change' : 'changes'} since you last looked`;
}

/* ---------------------------------------------------------------- */
/* The last thing that happened, and how long ago (UX round 10-13)   */
/* ---------------------------------------------------------------- */

export interface LastActivity {
  /** When (ISO). */
  at: string;
  /** The row's line, as the timeline names it. */
  text: string;
  /** Whole days since then. */
  days: number;
}

/** After this many days with nothing from a person, the card says it has
 *  gone quiet instead of showing a green tick. */
export const QUIET_DAYS = 14;

/**
 * The newest thing a person (or a delivery) did on the item, from its
 * loaded history; Merlin's housekeeping does not count. Null when the
 * window does not hold the newest rows or holds nothing but housekeeping.
 *
 * STATE01 (untouched since Sep 10) showed a green "Nothing new since you
 * last looked", and the Hartz case showed nothing at all on a first open;
 * the date of the last real activity was only in History at the bottom.
 */
export function lastActivity(
  window: TimelineWindow<DoorThreadEventRead> | null | undefined,
  now: Date = new Date(),
): LastActivity | null {
  if (!window || !(window.order === 'desc' || !window.has_more)) return null;
  const newest = window.events
    .filter((e) => !isHousekeeping(e) && !Number.isNaN(ms(e.created_at)))
    .reduce<DoorThreadEventRead | null>((best, e) => (!best || ms(e.created_at) > ms(best.created_at) ? e : best), null);
  if (!newest) return null;
  const days = Math.max(0, Math.floor((now.getTime() - ms(newest.created_at)) / 86_400_000));
  return { at: newest.created_at, text: timelineLine(newest), days };
}

/** The changes a person made: Merlin rewriting his notes 32 times is not
 *  a change Jess has to read (it is still listed, apart). */
export function peopleChangeCount(items: ReadonlyArray<Pick<SinceItem, 'kind'>>): number {
  return items.filter((i) => i.kind !== 'housekeeping').length;
}

/** "Last activity Sep 30, 13 days ago: Jess Finlaw: Ball moved" ("today"). */
export function lastActivityLine(last: LastActivity, now: Date = new Date()): string {
  const when = new Date(last.at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(new Date(last.at).getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  });
  const ago = last.days === 0 ? 'today' : last.days === 1 ? 'yesterday' : `${last.days} days ago`;
  return `Last activity ${last.days === 0 ? 'today' : `${when}, ${ago}`}: ${last.text}`;
}
